<script lang="ts">
  import { onMount } from "svelte"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import { userDisplayName } from "@kontextwerk/control-web-frame/logic/userDisplay"
  import type { OrganizationUser } from "@kontextwerk/control-web-client"

  import { updateWorkOrder } from "../../../../../../../../../../api/kehrblatt/work-orders"
  import { loadWorkOrderDetailsData } from "../../../../../../../../../../api/kehrblatt/work-order-detail"
  import type { ScheduleEventCommunicationPresentation } from "../../../../../../../../../../api/kehrblatt/work-orders"
  import { filterSelectableOrganizationUsers } from "../../../../../../../../_shared/_logic/organization-users"
  import type { WorkOrderPageContext as PageContext } from "../../../../context"
  import WorkOrderDetailsForm from "./_modules/work-order-details-form"

  const { pageContext }: { pageContext: PageContext } = $props()

  let loadedWorkOrder = $state<WorkOrder | null>(null)
  let properties = $state<Property[]>([])
  let customers = $state<Customer[]>([])
  let propertyCustomers = $state<PropertyCustomer[]>([])
  let contactPoints = $state<ContactPoint[]>([])
  let scheduleEvents = $state<ScheduleEvent[]>([])
  let scheduleEventCommunicationPresentations = $state<
    Record<string, ScheduleEventCommunicationPresentation>
  >({})
  let orderTypes = $state<OrderType[]>([])
  let organizationUsers = $state<OrganizationUser[]>([])
  let loading = $state(true)
  let saving = $state(false)
  let error = $state<string | null>(null)
  let requestId = 0

  const workOrder = $derived(pageContext.workOrder ?? loadedWorkOrder)
  const canEdit = $derived(
    Boolean(
      workOrder &&
      !pageContext.subscriptionRestrictionActive &&
      pageContext.permissions.allows("termin.scheduling", "manage") &&
      workOrder.can_manage &&
      !workOrder.archived_at,
    ),
  )
  const employees = $derived(
    filterSelectableOrganizationUsers(organizationUsers, null).map((user) => ({
      value: user.id,
      label: userDisplayName(user) || "Unbekannt",
    })),
  )

  const loadDetails = async () => {
    if (!pageContext.organizationId || !pageContext.workOrderId) return

    const currentRequest = ++requestId
    loading = true
    error = null
    try {
      const data = await loadWorkOrderDetailsData(
        pageContext.organizationId,
        pageContext.workOrderId,
      )

      if (currentRequest !== requestId) return

      loadedWorkOrder = data.workOrder
      properties = data.properties
      customers = data.customers
      propertyCustomers = data.propertyCustomers
      contactPoints = data.contactPoints
      scheduleEvents = data.scheduleEvents
      scheduleEventCommunicationPresentations = data.scheduleEventCommunicationPresentations
      orderTypes = data.orderTypes
      organizationUsers = data.users
      pageContext.onContextChange({ workOrder: loadedWorkOrder, properties, scheduleEvents })
    } catch (err) {
      if (currentRequest !== requestId) return

      console.error("work_order_details_content_load_failed", err)
      error = "Auftragsdetails konnten nicht geladen werden."
    } finally {
      if (currentRequest === requestId) loading = false
    }
  }

  const save = async (payload: WorkOrderUpdatePayload) => {
    if (!workOrder || !canEdit || saving) return

    saving = true
    try {
      const updated = await updateWorkOrder(workOrder.id, payload)
      loadedWorkOrder = updated
      pageContext.onContextChange({ workOrder: updated, properties, scheduleEvents })
      newNotification({ class: "success", html: "Auftrag aktualisiert." })
    } catch (err) {
      console.error("work_order_details_content_save_failed", err)
      newNotification({ class: "error", html: "Auftrag konnte nicht gespeichert werden." })
    } finally {
      saving = false
    }
  }

  onMount(() => {
    void loadDetails()

    return () => {
      requestId += 1
    }
  })
</script>

<WorkOrderDetailsForm
  organizationId={pageContext.organizationId}
  {workOrder}
  {properties}
  {customers}
  {propertyCustomers}
  {contactPoints}
  {scheduleEvents}
  {scheduleEventCommunicationPresentations}
  {orderTypes}
  {employees}
  {loading}
  {error}
  {saving}
  locked={!canEdit}
  {canEdit}
  onRetry={loadDetails}
  onSubmit={save}
  autoSave={true}
/>
